// src/FreelancerCard.js
import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { calculateProgressAndDays, getProgressBarCustomColor } from './utils';
import { API_BASE } from './api';
import { fileUrl } from './api';

/** ---- URL avatar: preserva le foto (assolute o relative) ---- **/
function resolveProfileImageUrl(raw, version) {
  if (!raw) return undefined;
  const v = version || Date.now(); // cache-buster
  // Se è già assoluto, uso così com'è
  if (/^https?:\/\//i.test(raw)) return `${raw}?v=${v}`;
  // Se parte da "/", usa l'origine del server API senza il suffisso /api
  const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');
  if (raw.startsWith('/')) return `${API_ORIGIN}${raw}?v=${v}`;
  // Altrimenti tratta come relativo a /uploads o percorso relativo salvato
  return `${API_ORIGIN}/${raw.replace(/^\/+/, '')}?v=${v}`;
  }

/** ---- Helpers date "pure" (niente timezone) ---- **/
const toDateOnly = (s) => {
  if (!s) return '';
  const str = String(s);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (isNaN(d)) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
};
const formatIT = (s) => {
  const ymd = toDateOnly(s);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

/** ---- Stile card ---- **/
const CardContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(1),
  alignItems: 'center',
  backgroundColor: '#13161c',
  padding: theme.spacing(1.25),
  borderRadius: 10,
  marginBottom: theme.spacing(0.75),
  border: '1px solid rgba(212,165,80,0.15)',
  boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
  cursor: 'pointer',
  transition: 'box-shadow .2s ease, transform .2s ease',
  '&:hover': {
    boxShadow: '0 10px 28px rgba(212,165,80,0.12)',
    transform: 'translateY(-1px)',
    borderColor: 'rgba(212,165,80,0.35)',
  },
}));

export default function FreelancerCard({ freelancer, onCardClick }) {
  const { status, progress, daysRemaining } = calculateProgressAndDays(
    freelancer.data_inizio_contratto,
    freelancer.data_fine_contratto
  );

  const isAvailable = status === 'Disponibile' && !freelancer.is_tentative;
  const isTentative = !!freelancer.is_tentative;

  const fullName = `${freelancer?.nome || ''} ${freelancer?.cognome || ''}`.trim();
  const roles = Array.isArray(freelancer?.ruoli) && freelancer.ruoli.length
    ? freelancer.ruoli
    : (freelancer?.ruolo ? [freelancer.ruolo] : []);

  const aziende = [];
  if (freelancer?.azienda) aziende.push(freelancer.azienda);
  // se vuoi, qui puoi pushare anche da maestranze

  return (
    <CardContainer onClick={onCardClick}>
      {/* Colonna 1: Avatar */}
      <Avatar
  src={fileUrl(
    freelancer.profile_image_url ||
    freelancer.profileImageUrl ||
    freelancer.image_url ||
    freelancer.avatar_url ||
    freelancer.avatar
  )}
  alt={fullName || 'Freelancer'}
  sx={{
    width: 40,
    height: 40,
    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
  }}
/>

      {/* Colonna 2: Dati principali */}
      <Box sx={{ minWidth: 0 }}>
        {/* Nome + ruoli */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: '#e8e8e8',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 1,
            }}
            title={fullName}
          >
            {fullName || '—'}
          </Typography>

          {roles.slice(0, 2).map((r) => (
            <Chip
              key={r}
              size="small"
              icon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
              label={r}
              sx={{
                fontWeight: 600,
                bgcolor: 'rgba(212,165,80,0.08)',
                border: '1px solid rgba(212,165,80,0.25)',
                color: '#d4a550',
              }}
            />
          ))}
          {roles.length > 2 && (
            <Chip size="small" label={`+${roles.length - 2}`} sx={{ fontWeight: 600 }} />
          )}
        </Stack>

        {/* Meta: azienda • programma • disponibilità */}
        <Stack direction="row" spacing={2} sx={{ color: '#888', flexWrap: 'wrap' }}>
          {aziende.length > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BusinessIcon sx={{ fontSize: 18, opacity: 0.8 }} />
              <Typography variant="body2">{aziende.join(' · ')}</Typography>
            </Stack>
          )}
          {freelancer.produzione && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {freelancer.produzione}
            </Typography>
          )}
          {freelancer.data_fine_contratto && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: 18, opacity: 0.8 }} />
              <Typography variant="body2">
                Disponibile da: {formatIT(freelancer.data_fine_contratto)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Colonna 3: Stato + progress + freccia */}
      <Box sx={{ minWidth: 210, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {isAvailable ? (
            <Typography variant="subtitle2" sx={{ color: '#4caf50', fontWeight: 800, letterSpacing: 0.5 }}>
              DISPONIBILE
            </Typography>
          ) : isTentative ? (
            <Typography variant="subtitle2" sx={{ color: '#a78bfa', fontWeight: 800, letterSpacing: 0.5 }}>
              NON CONFERMATA
            </Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                {Math.round(progress)}% ({daysRemaining} {daysRemaining === 1 ? 'giorno' : 'giorni'})
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Number.isFinite(progress) ? progress : 0}
                sx={{
                  mt: 0.5,
                  height: 8,
                  borderRadius: 20,
                  backgroundColor: '#2a2d35',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 20,
                    backgroundColor: getProgressBarCustomColor(progress),
                  },
                }}
              />
            </>
          )}
        </Box>

        <Box sx={{ color: '#d4a550', pl: 1, display: 'flex', alignItems: 'center', opacity: 0.7 }}>
          <ArrowForwardIosIcon />
        </Box>
      </Box>
    </CardContainer>
  );
}
